/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { Spin, message, Select, Button, DatePicker } from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import {
    BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, LabelList,
} from 'recharts';
import FleetService from '../../../services/FleetService';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const NAVY   = '#1a3a5c';
const COLORS = ['#1a3a5c','#2e86c1','#52c41a','#faad14','#f5222d','#722ed1','#13c2c2','#fa8c16'];
const toN    = v => Number(v || 0);
const fmtE   = v => `E ${toN(v).toLocaleString()}`;

function KPI({ label, value, sub, color = NAVY, pct }) {
    return (
        <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8,
            padding:'14px 18px', borderTop:`3px solid ${color}`, flex:1, minWidth:130 }}>
            <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>{label}</div>
            <div style={{ fontSize:22, fontWeight:700, color }}>{value}</div>
            {sub && <div style={{ fontSize:11, color:'#aaa', marginTop:2 }}>{sub}</div>}
            {pct != null && (
                <div style={{ marginTop:6, background:'#f0f0f0', borderRadius:4, height:5 }}>
                    <div style={{ width:`${Math.min(100,pct)}%`, background:color, height:5, borderRadius:4 }}/>
                </div>
            )}
        </div>
    );
}
function ChartCard({ title, children, height = 260 }) {
    return (
        <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8,
            padding:'14px 16px', marginBottom:14 }}>
            <div style={{ fontWeight:600, fontSize:13, color:NAVY, marginBottom:10 }}>{title}</div>
            <div style={{ height }}>{children}</div>
        </div>
    );
}

export default function FleetAnalyticsExec() {
    const [tco,     setTco]     = useState([]);
    const [status,  setStatus]  = useState([]);
    const [loading, setLoading] = useState(true);
    const [region,    setRegion]   = useState('');
    const [programme, setProgramme]= useState('');
    const [facility,  setFacility] = useState('');
    const [vehType,   setVehType]  = useState('');
    const [dateFrom,  setDateFrom] = useState('');
    const [dateTo,    setDateTo]   = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const [r1, r2] = await Promise.all([
                FleetService.reportTCO(),
                FleetService.reportStatus(),
            ]);
            if (Number(r1?.data?.code) === 200) setTco(r1.data.result    || []);
            if (Number(r2?.data?.code) === 200) setStatus(r2.data.result || []);
        } catch (e) { message.error('Failed to load analytics data'); }
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const regions    = useMemo(() => [...new Set(tco.map(r=>r.region).filter(Boolean))], [tco]);
    const programmes = useMemo(() => [...new Set(tco.map(r=>r.assigned_programme).filter(Boolean))], [tco]);
    const facilities = useMemo(() => [...new Set(tco.map(r=>r.facility).filter(Boolean))], [tco]);
    const vehTypes   = useMemo(() => [...new Set(tco.map(r=>r.vehicle_type).filter(Boolean))], [tco]);
    const filtered = useMemo(() => region ? tco.filter(r => r.region === region) : tco, [tco, region]);

    // ── KPIs ──────────────────────────────────────────────────────────────────
    const totalFleet      = status.length;
    const goodCond        = status.filter(v => /good|fair/i.test(v.functional_status || '')).length;
    const totalInvestment = filtered.reduce((s, r) => s + toN(r.procurement_cost), 0);
    const totalFuel       = filtered.reduce((s, r) => s + toN(r.fuel_cost), 0);
    const totalRepair     = filtered.reduce((s, r) => s + toN(r.repair_cost), 0);
    const totalBook       = Math.max(0, totalInvestment * 0.65); // rough book value

    const avgCpk = useMemo(() => {
        const valid = filtered.filter(r => toN(r.total_km) > 0 && toN(r.total_cost) > 0);
        if (!valid.length) return 0;
        return (valid.reduce((s,r) => s + toN(r.total_cost)/toN(r.total_km), 0) / valid.length).toFixed(2);
    }, [filtered]);

    // ── Top 10 stacked bar ────────────────────────────────────────────────────
    const top10 = useMemo(() =>
        [...filtered].sort((a,b) => toN(b.total_cost)-toN(a.total_cost)).slice(0,10).map(r => ({
            name:        r.registration_no || '—',
            Procurement: toN(r.procurement_cost),
            Fuel:        toN(r.fuel_cost),
            Repair:      toN(r.repair_cost),
            total:       toN(r.total_cost),
        }))
    , [filtered]);

    // ── Cost breakdown pie ────────────────────────────────────────────────────
    const costPie = [
        { name:'Procurement', value: totalInvestment },
        { name:'Fuel',        value: totalFuel       },
        { name:'Maintenance', value: totalRepair     },
    ].filter(d => d.value > 0);

    // ── Investment vs running by facility ─────────────────────────────────────
    const byFacility = useMemo(() => {
        const m = {};
        filtered.forEach(r => {
            const k = r.facility || 'Unknown';
            if (!m[k]) m[k] = { name:k, investment:0, running:0 };
            m[k].investment += toN(r.procurement_cost);
            m[k].running    += toN(r.fuel_cost) + toN(r.repair_cost);
        });
        return Object.values(m).sort((a,b) => b.investment-a.investment).slice(0,8);
    }, [filtered]);

    // ── Keep vs Replace ───────────────────────────────────────────────────────
    const keepReplace = useMemo(() =>
        filtered.map(r => {
            const repairPct = toN(r.procurement_cost) > 0
                ? (toN(r.repair_cost) / toN(r.procurement_cost)) * 100 : 0;
            const rec = repairPct >= 75 ? 'Replace' : 'Keep';
            return { ...r, repairPct, recommendation: rec };
        })
    , [filtered]);
    const keepCount    = keepReplace.filter(r => r.recommendation === 'Keep').length;
    const replaceCount = keepReplace.filter(r => r.recommendation === 'Replace').length;

    const exportXlsx = () => {
        const ws = XLSX.utils.json_to_sheet(filtered.map(r => ({
            'Reg No':         r.registration_no,
            'Type':           r.vehicle_type,
            'Facility':       r.facility,
            'Procurement (E)':r.procurement_cost,
            'Fuel (E)':       r.fuel_cost,
            'Repair (E)':     r.repair_cost,
            'Total Cost (E)': r.total_cost,
            'Total KM':       r.total_km,
        })));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Exec Summary');
        saveAs(new Blob([XLSX.write(wb,{bookType:'xlsx',type:'array'})]),
            `Fleet_Exec_${new Date().toISOString().slice(0,10)}.xlsx`);
    };

    return (
        <div className="page-content" style={{ padding:'20px 24px' }}>
            {/* Header */}
            <div style={{ display:'flex', justifyContent:'space-between',
                alignItems:'flex-start', marginBottom:20 }}>
                <div>
                    <h4 style={{ margin:0, fontWeight:700, color:NAVY }}>Executive Investment Summary</h4>
                    <div style={{ fontSize:12, color:'#888', marginTop:2 }}>
                        Fleet procurement vs running costs · cost-to-keep vs cost-to-replace analysis
                    </div>
                </div>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                    <Select allowClear placeholder="Region" size="small" style={{ width:120 }}
                        value={region||undefined} onChange={v=>setRegion(v||'')}>
                        {regions.map(r=><Select.Option key={r} value={r}>{r}</Select.Option>)}
                    </Select>
                    <Select allowClear placeholder="Programme" size="small" style={{ width:140 }}
                        value={programme||undefined} onChange={v=>setProgramme(v||'')}>
                        {programmes.map(p=><Select.Option key={p} value={p}>{p}</Select.Option>)}
                    </Select>
                    <Select allowClear placeholder="Facility" size="small" style={{ width:150 }}
                        value={facility||undefined} onChange={v=>setFacility(v||'')}>
                        {facilities.map(f=><Select.Option key={f} value={f}>{f}</Select.Option>)}
                    </Select>
                    <Select allowClear placeholder="Vehicle type" size="small" style={{ width:140 }}
                        value={vehType||undefined} onChange={v=>setVehType(v||'')}>
                        {vehTypes.map(t=><Select.Option key={t} value={t}>{t}</Select.Option>)}
                    </Select>
                    <DatePicker size="small" placeholder="From" style={{ width:110 }} onChange={(_,s)=>setDateFrom(s||'')} />
                    <DatePicker size="small" placeholder="To"   style={{ width:110 }} onChange={(_,s)=>setDateTo(s||'')} />
                    <Button size="small" icon={<ReloadOutlined/>} onClick={load}>Refresh</Button>
                    <Button size="small" type="primary" icon={<DownloadOutlined/>}
                        onClick={exportXlsx} disabled={!filtered.length}>Export</Button>
                </div>
            </div>

            <Spin spinning={loading}>
                {/* KPI row */}
                <div style={{ display:'flex', gap:10, marginBottom:18, flexWrap:'wrap' }}>
                    <KPI label="Total Fleet"           value={totalFleet}                color={NAVY} sub="vehicles"/>
                    <KPI label="Good / Fair"           value={goodCond}                  color="#52c41a"
                        sub={`${totalFleet?Math.round(goodCond/totalFleet*100):0}% operational`}
                        pct={totalFleet?goodCond/totalFleet*100:0}/>
                    <KPI label="Total Investment"      value={fmtE(totalInvestment)}     color="#722ed1" sub="procurement value"/>
                    <KPI label="Fuel Costs"            value={fmtE(totalFuel)}           color="#fa8c16"/>
                    <KPI label="Repair Costs"          value={fmtE(totalRepair)}         color="#f5222d"/>
                    <KPI label="Est. Book Value"       value={fmtE(totalBook)}           color="#2e86c1" sub="~65% of procurement"/>
                    <KPI label="Avg Cost / KM"         value={`E ${avgCpk}`}             color={avgCpk>5?'#f5222d':'#52c41a'}/>
                    <KPI label="Recommend Replace"     value={replaceCount}              color="#f5222d"
                        sub="repair ≥75% of procurement"
                        pct={filtered.length?replaceCount/filtered.length*100:0}/>
                </div>

                {/* Row 1: cost pie + top 10 bar */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:14, marginBottom:0 }}>
                    <ChartCard title="Fleet Cost Breakdown" height={280}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={costPie} dataKey="value" nameKey="name"
                                    cx="50%" cy="50%" outerRadius={95} labelLine={false}
                                    label={({name,percent})=>`${name} ${(percent*100).toFixed(0)}%`}>
                                    {costPie.map((_,i)=><Cell key={i} fill={COLORS[i]}/>)}
                                </Pie>
                                <Tooltip formatter={v=>[`E ${toN(v).toLocaleString()}`,'Cost']}/>
                                <Legend iconSize={10} wrapperStyle={{fontSize:11}}/>
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard title="Top 10 Vehicles — Stacked Costs (E)" height={280}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={top10} layout="vertical"
                                margin={{top:0,right:70,left:85,bottom:0}}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" horizontal={false}/>
                                <XAxis type="number" tick={{fontSize:10}}
                                    tickFormatter={v=>`E${(v/1000).toFixed(0)}k`}/>
                                <YAxis type="category" dataKey="name" tick={{fontSize:10}} width={80}/>
                                <Tooltip formatter={(v,n)=>[`E ${toN(v).toLocaleString()}`,n]}/>
                                <Legend wrapperStyle={{fontSize:10}}/>
                                <Bar dataKey="Procurement" stackId="a" fill={COLORS[0]}/>
                                <Bar dataKey="Fuel"        stackId="a" fill={COLORS[2]}/>
                                <Bar dataKey="Repair"      stackId="a" fill={COLORS[4]} radius={[0,3,3,0]}>
                                    <LabelList dataKey="total" position="right" style={{fontSize:9}}
                                        formatter={v=>`E${(v/1000).toFixed(0)}k`}/>
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>

                {/* Row 2: by facility + keep/replace */}
                <div style={{ display:'grid', gridTemplateColumns:'1.4fr 1fr', gap:14, marginTop:14 }}>
                    <ChartCard title="Investment vs Running by Facility (E)" height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={byFacility}
                                margin={{top:0,right:10,left:10,bottom:50}}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5"/>
                                <XAxis dataKey="name" tick={{fontSize:9}} angle={-30}
                                    textAnchor="end" height={55}/>
                                <YAxis tick={{fontSize:10}}
                                    tickFormatter={v=>`E${(v/1000).toFixed(0)}k`}/>
                                <Tooltip formatter={(v,n)=>[`E ${toN(v).toLocaleString()}`,n]}/>
                                <Legend wrapperStyle={{fontSize:10}}/>
                                <Bar dataKey="investment" name="Investment" fill={COLORS[0]} radius={[3,3,0,0]}/>
                                <Bar dataKey="running"    name="Running"    fill={COLORS[4]} radius={[3,3,0,0]}/>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard title="Keep vs Replace Recommendation" height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={[
                                    {name:'Keep',    value:keepCount},
                                    {name:'Replace', value:replaceCount},
                                ]} dataKey="value" nameKey="name"
                                    cx="50%" cy="45%" outerRadius={90}
                                    label={({name,value,percent})=>
                                        value>0?`${name}: ${value} (${(percent*100).toFixed(0)}%)`:''}>
                                    <Cell fill="#52c41a"/><Cell fill="#f5222d"/>
                                </Pie>
                                <Tooltip/>
                                <Legend wrapperStyle={{fontSize:11}}/>
                            </PieChart>
                        </ResponsiveContainer>
                        <div style={{fontSize:11,color:'#888',textAlign:'center'}}>
                            Criteria: repair cost ≥ 75% of procurement value
                        </div>
                    </ChartCard>
                </div>

                {/* Replace list banner */}
                {replaceCount > 0 && (
                    <div style={{background:'#fff1f0',border:'1px solid #ffa39e',borderRadius:8,
                        padding:'12px 16px',marginTop:14}}>
                        <div style={{fontWeight:600,color:'#cf1322',marginBottom:8}}>
                            🔴 {replaceCount} vehicle{replaceCount>1?'s':''} recommended for replacement
                        </div>
                        <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                            {keepReplace.filter(r=>r.recommendation==='Replace').map((r,i)=>(
                                <div key={i} style={{background:'#fff',border:'1px solid #ffa39e',
                                    borderRadius:6,padding:'5px 10px',fontSize:11}}>
                                    <strong>{r.registration_no||'—'}</strong>
                                    <span style={{color:'#888',marginLeft:6}}>
                                        repair {r.repairPct.toFixed(0)}% of cost
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {!loading && filtered.length === 0 && (
                    <div style={{textAlign:'center',padding:60,color:'#aaa',
                        border:'2px dashed #e8e8e8',borderRadius:8,marginTop:14}}>
                        <div style={{fontSize:32,marginBottom:12}}>📊</div>
                        <div style={{fontWeight:600,marginBottom:4}}>No cost data yet</div>
                        <div style={{fontSize:12}}>
                            Record procurement costs and maintenance entries to see analytics.
                        </div>
                    </div>
                )}
            </Spin>
        </div>
    );
}